import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';

@customElement('product-component')
class ProductComponent extends LitElement {
  @state()
  accessor productColorGrouping = {};

  @property({ type: String, attribute: 'section-id' })
  accessor sectionId;

  @property({ type: Array, attribute: 'app-color-variants' })
  accessor appColorVariants;

  connectedCallback() {
    super.connectedCallback();
    this._buildColorSectionCache();
    this.sectionsToReinitialize = ['product-media-gallery'];
    window.addEventListener('swatch:change', this._handleSwatchUpdate);
    this.addEventListener('pdp:variant-change', this._handleVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('swatch:change', this._handleSwatchUpdate);
    this.removeEventListener('pdp:variant-change', this._handleVariant);
  }

  _handleVariantChange = (event) => {
    const { value } = event.detail;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', value);
    window.history.replaceState({}, '', url);
  };

  _buildColorSectionCache = async () => {
    if (!this.appColorVariants) return;

    const fetchPromises = this.appColorVariants.map(async (handle) => {
      if (!this.productColorGrouping[handle]) {
        const response = await fetch(
          // eslint-disable-next-line max-len
          `${window.Shopify.routes.root}products/${handle}?sections=${this.sectionId}`,
        );
        const json = await response.json();
        this.productColorGrouping = {
          ...this.productColorGrouping,
          [handle]: json[this.sectionId],
        };
      }
    });
    await Promise.all(fetchPromises);
  };

  _handleSwatchUpdate = async (event) => {
    const { handle } = event.detail.product;
    const textMarkup = this.productColorGrouping[handle];
    const parser = new DOMParser();
    const dom = parser.parseFromString(textMarkup, 'text/html');
    const sectionsToReplace = this.querySelectorAll(
      '[data-replacement-target]',
    );

    sectionsToReplace.forEach((section) => {
      const newSection = dom.querySelector(
        `[data-replacement-target="${section.dataset.replacementTarget}"]`,
      );
      section.replaceWith(newSection);
      if (
        newSection.parentElement &&
        typeof newSection.parentElement.initializeComponent === 'function'
      ) {
        newSection.parentElement.initializeComponent();
      }
    });

    const url = new URL(window.location.href);
    url.pathname = `${window.Shopify.routes.root}products/${handle}`;
    url.searchParams.delete('variant');
    window.history.replaceState({}, '', url);

    this._handleElevarDataLayerPush({ handle, dom });
  };

  _handleElevarDataLayerPush = ({ dom }) => {
    const sectionJSON = JSON.parse(
      dom.querySelector('[data-section-data]').innerText,
    );
    const productJSON = sectionJSON.product;
    const {
      id,
      title,
      collectionBreadcrumbHandle,
      variants,
      imageURL,
      vendor,
      productType,
      priceRange,
      compareAtPriceRange,
    } = productJSON;
    const productAvailability = {};
    const updatedCollection = `/collections/${
      collectionBreadcrumbHandle || ''
    }`;
    const firstVariant = variants[0];
    window.ElevarTransformFn = null;

    for (const variant of variants) {
      const { availableForSale } = variant;
      const sizeOption = variant.selectedOptions.find(
        (option) => option.name === 'Size',
      );
      if (sizeOption) {
        productAvailability[sizeOption.value] = availableForSale ? 1 : 0;
      }
    }

    const availabilityString = JSON.stringify(productAvailability);
    window.ElevarTransformFn = null;

    const listItems = [
      {
        brand: vendor,
        category: productType,
        compare_at_price: String(compareAtPriceRange.minVariantPrice.amount),
        id: firstVariant.sku,
        image: imageURL,
        list: updatedCollection,
        name: title,
        quantity: '1',
        price: String(priceRange.minVariantPrice.amount),
        product_id: id.split('gid://shopify/Product/')[1] || null,
        variant: firstVariant.title,
        variant_id:
          firstVariant.id.split('gid://shopify/ProductVariant/')[1] || null,
      },
    ];

    const eventData = {
      event: 'dl_view_item',
      user_properties: {},
      availability: availabilityString,
      ecommerce: {
        currencyCode: priceRange.minVariantPrice.currencyCode,
        detail: {
          actionField: {
            list: updatedCollection,
            action: 'detail',
          },
          products: listItems,
        },
      },
    };
    window.ElevarDataLayer.push(eventData);
  };

  render() {
    return html`<slot></slot>`;
  }
}

export default ProductComponent;
